import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { 
  getOfferMatches, 
  createTradeRequest, 
  getTradeRequests, 
  updateTradeRequestStatus,
  getSafeUsers,
  getUserOfferCards
} from '../../services/supabaseService';

export const Trades: React.FC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const location = useLocation();
  const navigatedStateHandled = useRef<boolean>(false);
  const [matches, setMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'matches' | 'active' | 'history' | 'users'>('matches');
  const [matchFilter, setMatchFilter] = useState<'all' | 'wishlist_to_offer' | 'offer_to_wish' | 'mutual'>('all');

  // Community safe users state from get_safe_users RPC
  const [safeUsers, setSafeUsers] = useState<any[]>([]);
  const [loadingSafeUsers, setLoadingSafeUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userFilter, setUserFilter] = useState<'all' | 'offers' | 'wishlist'>('all');

  // Contact requests state
  const [tradeRequests, setTradeRequests] = useState<any[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  // Contact modal state
  const [selectedTarget, setSelectedTarget] = useState<any | null>(null);
  const [modalAvailableCards, setModalAvailableCards] = useState<any[]>([]);
  const [loadingModalCards, setLoadingModalCards] = useState(false);
  const [markedCardNames, setMarkedCardNames] = useState<string[]>([]);
  const [customCardInput, setCustomCardInput] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasPhone = Boolean(user?.phone && String(user.phone).trim().length > 0);

  useEffect(() => {
    if (user) {
      loadSafeUsers().then((usersList) => {
        loadMatches(usersList);
      });
      loadRequests();
    }
  }, [user]);

  useEffect(() => {
    if (!user || navigatedStateHandled.current) return;
    const state = location.state as any;
    if (state?.targetUser) {
      navigatedStateHandled.current = true;
      const initialCards = state.initialCard 
        ? [state.initialCard] 
        : (Array.isArray(state.initialCards) ? state.initialCards : []);
      handleOpenContactModalForUser(state.targetUser, initialCards);
    }
  }, [user, location.state]);

  const loadSafeUsers = async (): Promise<any[]> => {
    setLoadingSafeUsers(true);
    try {
      const list = await getSafeUsers();
      setSafeUsers(list || []);
      return list || [];
    } catch (err) {
      console.error('Error loading safe users in Cardlink:', err);
      return [];
    } finally {
      setLoadingSafeUsers(false);
    }
  };

  const loadMatches = async (availableSafeUsers?: any[]) => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await getOfferMatches(user.id);
      const list = availableSafeUsers || safeUsers;
      if (list && list.length > 0) {
        const usersMap = new Map(list.map((u: any) => [Number(u.id), u]));
        const enriched = (data || []).map((m: any) => {
          const profile = usersMap.get(Number(m.matched_user_id));
          if (!profile) return m;
          return {
            ...m,
            matched_username: profile.username || m.matched_username,
            matched_avatar: profile.avatar || m.matched_avatar,
            fullname: profile.fullname || m.fullname,
            vip: profile.vip
          };
        });
        setMatches(enriched);
      } else {
        setMatches(data || []);
      }
    } catch (err) {
      console.error('Error loading matches:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    if (!user) return;
    setLoadingRequests(true);
    try {
      const data = await getTradeRequests();
      setTradeRequests(data || []);
    } catch (err) {
      console.error('Error loading trade requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  };

  const hasPendingRequest = (matchedUserId: any) => {
    return tradeRequests.some((r: any) => 
      r.status === 'pending' && 
      ((String(r.requester_id) === String(user?.id) && String(r.receiver_id) === String(matchedUserId)) ||
       (String(r.receiver_id) === String(user?.id) && String(r.requester_id) === String(matchedUserId)))
    );
  };

  /**
   * Abre o modal de solicitação de contato a partir de um Match automático
   */
  const handleOpenContactModalFromMatch = (match: any, preferredType?: 'wishlist_to_offer' | 'offer_to_wish' | 'all' | 'mutual') => {
    if (hasPendingRequest(match.matched_user_id)) {
      showNotification('Você já possui uma solicitação de contato pendente com este usuário.', 'error');
      return;
    }
    if (!hasPhone) {
      showNotification('Cadastre seu telefone no perfil para facilitar o contato pelo WhatsApp!', 'info');
    }

    const effectiveFilter = preferredType || matchFilter;
    const hasWishlist = Array.isArray(match.wishlist_matches) && match.wishlist_matches.length > 0;
    const hasOfferlist = Array.isArray(match.offerlist_matches) && match.offerlist_matches.length > 0;

    let cardsToMark: any[] = [];
    let initialMessage = '';

    if (effectiveFilter === 'offer_to_wish' && hasOfferlist) {
      cardsToMark = match.offerlist_matches;
      initialMessage = `Olá @${match.matched_username}! Vi que você tem interesse na(s) carta(s): ${cardsToMark.map((c: any) => c.name).join(', ')} da minha Offerlist na sua Wishlist. Gostaria de entrar em contato para combinarmos a negociação.`;
    } else if (effectiveFilter === 'wishlist_to_offer' && hasWishlist) {
      cardsToMark = match.wishlist_matches;
      initialMessage = `Olá @${match.matched_username}! Vi que você possui a(s) carta(s): ${cardsToMark.map((c: any) => c.name).join(', ')} na sua Offerlist que estão na minha Wishlist. Gostaria de entrar em contato para combinarmos a negociação.`;
    } else if (match.is_mutual) {
      cardsToMark = match.matching_cards || [];
      initialMessage = `Olá @${match.matched_username}! O CardLink identificou uma oportunidade de troca mútua entre nós: temos cartas de interesse recíproco nas nossas pastas. Gostaria de conversar para combinarmos a negociação!`;
    } else if (hasWishlist && !hasOfferlist) {
      cardsToMark = match.wishlist_matches;
      initialMessage = `Olá @${match.matched_username}! Vi que você possui a(s) carta(s): ${cardsToMark.map((c: any) => c.name).join(', ')} na sua Offerlist que estão na minha Wishlist. Gostaria de entrar em contato para combinarmos a negociação.`;
    } else if (hasOfferlist && !hasWishlist) {
      cardsToMark = match.offerlist_matches;
      initialMessage = `Olá @${match.matched_username}! Vi que você tem interesse na(s) carta(s): ${cardsToMark.map((c: any) => c.name).join(', ')} da minha Offerlist na sua Wishlist. Gostaria de entrar em contato para combinarmos a negociação.`;
    } else {
      cardsToMark = match.matching_cards || [];
      initialMessage = `Olá @${match.matched_username}! Tenho interesse nas cartas marcadas acima. Gostaria de entrar em contato para combinarmos a negociação.`;
    }

    setSelectedTarget({
      id: match.matched_user_id,
      username: match.matched_username,
      fullname: match.fullname,
      avatar: match.matched_avatar
    });
    setModalAvailableCards(match.matching_cards && match.matching_cards.length > 0 ? match.matching_cards : cardsToMark);
    setMarkedCardNames(cardsToMark.map((c: any) => c.name));
    setCustomCardInput('');
    setCustomMessage(initialMessage);
  };

  /**
   * Abre o modal de solicitação de contato a partir da aba de Colecionadores ou navegação externa
   */
  const handleOpenContactModalForUser = async (targetUser: any, initialCards: string[] = []) => {
    if (hasPendingRequest(targetUser.id)) {
      showNotification('Você já possui uma solicitação de contato pendente com este usuário.', 'error');
      return;
    }
    if (!hasPhone) {
      showNotification('Cadastre seu telefone no perfil para facilitar o contato pelo WhatsApp!', 'info');
    }

    const resolvedUsername = targetUser.username || targetUser.displayName || 'colecionador';

    setSelectedTarget({
      id: targetUser.id,
      username: resolvedUsername,
      fullname: targetUser.fullname || targetUser.displayName || resolvedUsername,
      avatar: targetUser.avatar || targetUser.photoURL
    });
    setModalAvailableCards([]);
    setMarkedCardNames(initialCards);
    setCustomCardInput('');
    setCustomMessage(
      initialCards.length > 0
        ? `Olá @${resolvedUsername}! Vi seu fichário e marquei interesse na(s) carta(s): ${initialCards.join(', ')}. Gostaria de entrar em contato para combinarmos a negociação.`
        : `Olá @${resolvedUsername}! Vi seu perfil no CardLink e tenho interesse em entrar em contato com você para negociarmos.`
    );
    
    // Busca as cartas que o usuário tem na offerlist ou coleção
    setLoadingModalCards(true);
    try {
      const cards = await getUserOfferCards(targetUser.id);
      const fetchedCards = cards || [];
      
      // Se cartas iniciais foram passadas (ex: marcadas na pasta), garante que apareçam na lista do modal
      if (initialCards.length > 0) {
        const existingNames = fetchedCards.map((c: any) => c.name);
        const extraCards = initialCards
          .filter(name => !existingNames.includes(name))
          .map(name => ({ id: `folder-${name}`, name }));
        setModalAvailableCards([...extraCards, ...fetchedCards]);
      } else {
        setModalAvailableCards(fetchedCards);
      }
    } catch (err) {
      console.warn('Erro ao carregar cartas do usuário para o modal:', err);
      if (initialCards.length > 0) {
        setModalAvailableCards(initialCards.map(name => ({ id: `folder-${name}`, name })));
      }
    } finally {
      setLoadingModalCards(false);
    }
  };

  const toggleCardMark = (cardName: string) => {
    setMarkedCardNames(prev => 
      prev.includes(cardName) 
        ? prev.filter(n => n !== cardName) 
        : [...prev, cardName]
    );
  };

  const handleAddCustomCard = () => {
    const trimmed = customCardInput.trim();
    if (!trimmed) return;
    if (!markedCardNames.includes(trimmed)) {
      setMarkedCardNames(prev => [...prev, trimmed]);
    }
    setCustomCardInput('');
  };

  const handleSelectAllCards = () => {
    const allNames = Array.from(new Set(modalAvailableCards.map(c => c.name).filter(Boolean)));
    setMarkedCardNames(allNames);
  };

  const handleClearAllCards = () => {
    setMarkedCardNames([]);
  };

  const handleSendContactRequest = async () => {
    if (!selectedTarget) return;
    if (markedCardNames.length === 0 && !customMessage.trim()) {
      showNotification('Selecione ao menos uma carta de interesse ou escreva uma mensagem.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      // Monta a mensagem estruturada com as cartas marcadas
      let formatted = '';
      if (markedCardNames.length > 0) {
        formatted += `[INTERESSE]\n${markedCardNames.join('\n')}\n[/INTERESSE]\n\n`;
      }
      formatted += customMessage.trim();
      if (user?.phone) {
        formatted += `\n\n[SOLICITANTE_PHONE:${user.phone.trim()}]`;
      }

      await createTradeRequest(selectedTarget.id, formatted);
      showNotification('Solicitação de contato enviada com sucesso! O número de WhatsApp só ficará visível se for aceita.', 'success');
      setSelectedTarget(null);
      await loadRequests();
      setActiveTab('active');
    } catch (err) {
      console.error('Error sending contact request:', err);
      showNotification('Infelizmente ocorreu um erro ao enviar a solicitação.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateStatus = async (
    target: any, 
    status: 'accepted' | 'declined' | 'rejected' | 'cancelled'
  ) => {
    try {
      const requestId = typeof target === 'object' && target?.id ? target.id : target;
      const targetReq = typeof target === 'object' && target?.id ? target : tradeRequests.find((r: any) => r.id === requestId);
      const dbStatus = status === 'declined' ? 'rejected' : status;

      let updatedMessage: string | undefined = undefined;
      if (dbStatus === 'accepted' && targetReq) {
        const currentMsg = targetReq.message || '';
        // Anexa o telefone do destinatário que está aceitando (se cadastrado e ainda não presente)
        if (user?.phone && !currentMsg.includes('[DESTINATARIO_PHONE:')) {
          updatedMessage = `${currentMsg}\n\n[DESTINATARIO_PHONE:${user.phone.trim()}]`;
        }
      }

      await updateTradeRequestStatus(requestId, dbStatus, updatedMessage);
      const statusLabels: Record<string, string> = {
        accepted: 'Contato aceito! Os números de contato de ambas as partes foram liberados.',
        declined: 'Solicitação recusada.',
        rejected: 'Solicitação recusada.',
        cancelled: 'Solicitação cancelada com sucesso.'
      };
      showNotification(statusLabels[status] || 'Status atualizado com sucesso!', 'success');
      await loadRequests();
    } catch (err) {
      console.error('Error updating contact request status:', err);
      showNotification('Não foi possível atualizar o status da solicitação.', 'error');
    }
  };

  /**
   * Analisa mensagens salvas para extrair cartas de interesse marcadas e telefones de ambas as partes.
   * Remove quaisquer telefones do texto livre para que não vazem antes do aceite.
   */
  const parseContactMessage = (rawMessage: string) => {
    if (!rawMessage) return { cards: [], text: '', requesterPhone: '', receiverPhone: '' };

    let cards: string[] = [];
    let text = rawMessage;
    let requesterPhone = '';
    let receiverPhone = '';

    // 1. Extrai cartas de interesse
    const matchCards = text.match(/\[(?:INTERESSE|CARTAS_INTERESSE)\]([\s\S]*?)\[\/(?:INTERESSE|CARTAS_INTERESSE)\]/i);
    if (matchCards) {
      cards = matchCards[1]
        .split('\n')
        .map(c => c.trim().replace(/^[•\-\*]\s*/, ''))
        .filter(Boolean);
      text = text.replace(/\[(?:INTERESSE|CARTAS_INTERESSE)\][\s\S]*?\[\/(?:INTERESSE|CARTAS_INTERESSE)\]/i, '').trim();
    }

    // 2. Extrai telefone do solicitante (tag estruturada ou formato legado)
    const matchReqPhone = text.match(/\[SOLICITANTE_PHONE:\s*([^\]]+)\]/i);
    if (matchReqPhone) {
      requesterPhone = matchReqPhone[1].trim();
      text = text.replace(/\[SOLICITANTE_PHONE:\s*[^\]]+\]/gi, '').trim();
    } else {
      const phoneMatch = text.match(/(?:WhatsApp para contato|WhatsApp|Contato|Telefone):\s*([0-9()\s+-]+)/i);
      if (phoneMatch) {
        requesterPhone = phoneMatch[1].trim();
      }
    }

    // 3. Extrai telefone do destinatário (anexado no aceite)
    const matchRecPhone = text.match(/\[DESTINATARIO_PHONE:\s*([^\]]+)\]/i);
    if (matchRecPhone) {
      receiverPhone = matchRecPhone[1].trim();
      text = text.replace(/\[DESTINATARIO_PHONE:\s*[^\]]+\]/gi, '').trim();
    } else {
      const recPhoneMatch = text.match(/(?:WhatsApp do destinatário|WhatsApp Destinatário|Contato Destinatário):\s*([0-9()\s+-]+)/i);
      if (recPhoneMatch) {
        receiverPhone = recPhoneMatch[1].trim();
      }
    }

    // 4. Higieniza o texto livre removendo linhas de telefone para proteger a privacidade antes do aceite
    text = text
      .replace(/(?:WhatsApp para contato|WhatsApp do destinatário|WhatsApp|Contato|Telefone):\s*[0-9()\s+-]+/gi, '')
      .replace(/\[(?:SOLICITANTE_PHONE|DESTINATARIO_PHONE):[^\]]*\]/gi, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return { cards, text, requesterPhone, receiverPhone };
  };

  const getWhatsAppLink = (rawPhone: string, partnerName?: string) => {
    const digits = rawPhone.replace(/\D/g, '');
    if (!digits || digits.length < 8) return null;
    const fullNumber = digits.length <= 11 ? `55${digits}` : digits;
    const greeting = partnerName 
      ? `Olá ${partnerName}! Vi sua solicitação de contato no Cardumy e gostaria de conversarmos.` 
      : 'Olá! Vi sua solicitação de contato no Cardumy e gostaria de conversarmos.';
    return `https://wa.me/${fullNumber}?text=${encodeURIComponent(greeting)}`;
  };

  // Filter community users from get_safe_users RPC
  const filteredUsers = safeUsers.filter((u: any) => {
    if (user && (Number(u.id) === Number(user.id) || String(u.username) === String(user.username))) {
      return false;
    }
    if (userFilter === 'offers' && (!u.offers_size || u.offers_size <= 0)) {
      return false;
    }
    if (userFilter === 'wishlist' && (!u.wishlist_size || u.wishlist_size <= 0)) {
      return false;
    }
    if (!userSearchTerm.trim()) return true;
    const term = userSearchTerm.toLowerCase().trim().replace(/^@/, '');
    const uname = (u.username || '').toLowerCase();
    const fname = (u.fullname || '').toLowerCase();
    return uname.includes(term) || fname.includes(term);
  });

  // Filter requests for each view
  const activeIncoming = tradeRequests.filter((r: any) => r.receiver_id === user?.id && r.status === 'pending');
  const activeOutgoing = tradeRequests.filter((r: any) => r.requester_id === user?.id && r.status === 'pending');
  const historyRequests = tradeRequests.filter((r: any) => r.status && r.status !== 'pending');

  // Contadores e filtragem de oportunidades
  const countAllMatches = matches.length;
  const countWishlistToOffer = matches.filter(m => Array.isArray(m.wishlist_matches) && m.wishlist_matches.length > 0).length;
  const countOfferToWish = matches.filter(m => Array.isArray(m.offerlist_matches) && m.offerlist_matches.length > 0).length;
  const countMutual = matches.filter(m => Boolean(m.is_mutual)).length;

  const filteredMatches = matches.filter(m => {
    if (matchFilter === 'wishlist_to_offer') {
      return Array.isArray(m.wishlist_matches) && m.wishlist_matches.length > 0;
    }
    if (matchFilter === 'offer_to_wish') {
      return Array.isArray(m.offerlist_matches) && m.offerlist_matches.length > 0;
    }
    if (matchFilter === 'mutual') {
      return Boolean(m.is_mutual);
    }
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 animate-in fade-in duration-500 px-2 sm:px-4 pb-16">
      {/* Header Principal Padronizado */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 border-b border-slate-800/80 pb-4 sm:pb-6">
        <div>
          <div className="flex items-center space-x-3 mb-1">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white shadow-lg shadow-purple-600/20 shrink-0">
              <i className="fas fa-arrow-right-arrow-left text-base sm:text-lg"></i>
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-white tracking-tight">
                Cardlink & Trocas
              </h1>
              <p className="text-[11px] sm:text-xs md:text-sm text-slate-400">
                Realize trocas diretas e encontre combinações com outros membros do Cardumy.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-start sm:justify-end gap-2.5">
          <button
            onClick={() => {
              loadSafeUsers().then((usersList) => loadMatches(usersList));
              loadRequests();
            }}
            disabled={loading || loadingSafeUsers || loadingRequests}
            className="flex items-center space-x-1.5 px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 hover:text-white text-xs font-bold transition-all cursor-pointer shadow-sm active:scale-95 disabled:opacity-50"
            title="Recarregar oportunidades de troca"
          >
            <i className={`fas fa-rotate text-purple-400 text-xs ${loading || loadingSafeUsers || loadingRequests ? 'animate-spin' : ''}`}></i>
            <span>Atualizar</span>
          </button>
        </div>
      </div>

      {/* KPI Cards de Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-600/10 border border-purple-500/20 flex items-center justify-center text-purple-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-handshake"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Oportunidades</p>
            <p className="text-lg sm:text-2xl font-black text-white">{countAllMatches}</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-600/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-arrows-rotate"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Trocas Mútuas</p>
            <p className="text-lg sm:text-2xl font-black text-emerald-400">{countMutual}</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-600/10 border border-amber-500/20 flex items-center justify-center text-amber-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-clock"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Pendentes</p>
            <p className="text-lg sm:text-2xl font-black text-amber-400">{activeIncoming.length + activeOutgoing.length}</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center text-blue-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-users"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Colecionadores</p>
            <p className="text-lg sm:text-2xl font-black text-blue-400">{safeUsers.length}</p>
          </div>
        </div>
      </div>

      {/* Alerta de telefone não cadastrado */}
      {user && !hasPhone && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-3xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 backdrop-blur-sm shadow-xl shadow-amber-500/5">
          <div className="flex items-start sm:items-center space-x-4">
            <div className="w-11 h-11 rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 text-lg shrink-0">
              <i className="fas fa-phone-slash"></i>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-white font-black text-sm sm:text-base">Telefone não cadastrado</h3>
                <span className="text-[9px] font-black uppercase tracking-widest bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full border border-amber-500/30">
                  Recomendado
                </span>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed max-w-2xl">
                Cadastre seu número no perfil para que outros colecionadores possam iniciar a conversa pelo WhatsApp ao aceitarem sua solicitação de contato.
              </p>
            </div>
          </div>
          <Link
            to="/perfil/editar"
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-black px-5 py-2.5 rounded-2xl text-xs uppercase tracking-widest transition-all shadow-lg shadow-amber-500/20 active:scale-95 flex items-center justify-center space-x-2 shrink-0 whitespace-nowrap"
          >
            <i className="fas fa-phone"></i>
            <span>Cadastrar Número</span>
          </Link>
        </div>
      )}

      {/* Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="col-span-1 lg:col-span-2 space-y-6">
           {/* Navigation Tabs */}
           <div className="flex items-center space-x-2 sm:space-x-4 pb-3 border-b border-slate-800 overflow-x-auto scrollbar-none">
              <button 
                onClick={() => setActiveTab('matches')}
                className={`text-xs sm:text-sm font-black uppercase tracking-widest pb-2 transition-all shrink-0 cursor-pointer ${activeTab === 'matches' ? 'text-white border-b-2 border-purple-600' : 'text-slate-500 hover:text-slate-300'}`}
              >
                Matches
              </button>
              <button 
                onClick={() => setActiveTab('active')}
                className={`text-xs sm:text-sm font-black uppercase tracking-widest pb-2 transition-all relative shrink-0 cursor-pointer ${activeTab === 'active' ? 'text-white border-b-2 border-purple-600' : 'text-slate-500 hover:text-slate-300'}`}
              >
                <span>Pendentes</span>
                {(activeIncoming.length + activeOutgoing.length) > 0 && (
                  <span className="ml-1.5 bg-purple-500/20 text-purple-400 border border-purple-500/30 text-[9px] font-mono px-1.5 py-0.5 rounded-full">
                    {activeIncoming.length + activeOutgoing.length}
                  </span>
                )}
              </button>
              <button 
                onClick={() => setActiveTab('history')}
                className={`text-xs sm:text-sm font-black uppercase tracking-widest pb-2 transition-all shrink-0 cursor-pointer ${activeTab === 'history' ? 'text-white border-b-2 border-purple-600' : 'text-slate-500 hover:text-slate-300'}`}
              >
                Histórico
              </button>
              <button 
                onClick={() => setActiveTab('users')}
                className={`text-xs sm:text-sm font-black uppercase tracking-widest pb-2 transition-all flex items-center gap-1.5 shrink-0 cursor-pointer ${activeTab === 'users' ? 'text-white border-b-2 border-purple-600' : 'text-slate-500 hover:text-slate-300'}`}
              >
                <span>Colecionadores</span>
                <span className="text-[10px] bg-slate-800 text-purple-400 font-mono px-2 py-0.5 rounded-full border border-white/5">
                  {safeUsers.length}
                </span>
              </button>
           </div>

           {/* 1. ABA OPORTUNIDADES (MATCHES) */}
           {activeTab === 'matches' ? (
             <div className="space-y-4">
               {/* FILTROS DE COMBINAÇÕES DO CARDLINK */}
               <div className="bg-slate-900 border border-slate-800/80 rounded-2xl p-2 sm:p-2.5 flex flex-wrap gap-2 items-center">
                 <span className="text-[10px] font-black uppercase text-slate-500 tracking-wider px-2 hidden sm:inline">
                   Filtrar por:
                 </span>
                 
                 {/* Todas as Oportunidades */}
                 <button
                   type="button"
                   onClick={() => setMatchFilter('all')}
                   className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                     matchFilter === 'all'
                       ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                       : 'bg-slate-950/60 text-slate-400 hover:text-white hover:bg-slate-800 border border-white/5'
                   }`}
                 >
                   <span>Tudo</span>
                   <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded-full ${
                     matchFilter === 'all' ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-400'
                   }`}>
                     {countAllMatches}
                   </span>
                 </button>

                 {/* Possuem da Minha Wishlist */}
                 <button
                   type="button"
                   onClick={() => setMatchFilter('wishlist_to_offer')}
                   className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                     matchFilter === 'wishlist_to_offer'
                       ? 'bg-pink-600 text-white shadow-md shadow-pink-600/30'
                       : 'bg-slate-950/60 text-slate-400 hover:text-white hover:bg-slate-800 border border-white/5'
                   }`}
                   title="Usuários que possuem na Offerlist deles cartas da sua Wishlist"
                 >
                   <i className={`fas fa-heart text-xs ${matchFilter === 'wishlist_to_offer' ? 'text-white' : 'text-pink-400'}`}></i>
                   <span>Têm na Wishlist</span>
                   <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded-full ${
                     matchFilter === 'wishlist_to_offer' ? 'bg-white/20 text-white' : 'bg-slate-800 text-pink-300'
                   }`}>
                     {countWishlistToOffer}
                   </span>
                 </button>

                 {/* Desejam da Minha Offerlist */}
                 <button
                   type="button"
                   onClick={() => setMatchFilter('offer_to_wish')}
                   className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                     matchFilter === 'offer_to_wish'
                       ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
                       : 'bg-slate-950/60 text-slate-400 hover:text-white hover:bg-slate-800 border border-white/5'
                   }`}
                   title="Usuários que possuem na Wishlist deles cartas da sua Offerlist"
                 >
                   <i className={`fas fa-arrow-right-arrow-left text-xs ${matchFilter === 'offer_to_wish' ? 'text-white' : 'text-emerald-400'}`}></i>
                   <span>Quer da Offerlist</span>
                   <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded-full ${
                     matchFilter === 'offer_to_wish' ? 'bg-white/20 text-white' : 'bg-slate-800 text-emerald-300'
                   }`}>
                     {countOfferToWish}
                   </span>
                 </button>

                 {/* Trocas Mútuas */}
                 <button
                   type="button"
                   onClick={() => setMatchFilter('mutual')}
                   className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                     matchFilter === 'mutual'
                       ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30'
                       : 'bg-slate-950/60 text-slate-400 hover:text-white hover:bg-slate-800 border border-white/5'
                   }`}
                   title="Usuários com interesse recíproco nas duas pastas"
                 >
                   <i className={`fas fa-handshake text-xs ${matchFilter === 'mutual' ? 'text-white' : 'text-indigo-400'}`}></i>
                   <span>Troca Mútua</span>
                   <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded-full ${
                     matchFilter === 'mutual' ? 'bg-white/20 text-white' : 'bg-slate-800 text-indigo-300'
                   }`}>
                     {countMutual}
                   </span>
                 </button>
               </div>

               {loading ? (
                 <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-12 text-center">
                    <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Buscando oportunidades de contato...</p>
                 </div>
               ) : filteredMatches.length > 0 ? (
                 <div className="grid grid-cols-1 gap-4">
                   {filteredMatches.map((match, idx) => {
                     const hasWishlist = Array.isArray(match.wishlist_matches) && match.wishlist_matches.length > 0;
                     const hasOfferlist = Array.isArray(match.offerlist_matches) && match.offerlist_matches.length > 0;

                     return (
                       <div key={idx} className="bg-slate-900 border border-slate-800/60 rounded-3xl p-6 hover:border-purple-500/30 transition-all group">
                          {/* Header do usuário */}
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                             <Link 
                               to={`/perfil/${match.matched_user_id}`} 
                               className="flex items-center space-x-3.5 cursor-pointer hover:opacity-90 transition-opacity group/profile"
                             >
                                <div className="w-12 h-12 bg-slate-800 rounded-2xl overflow-hidden border border-white/5 transition-transform group-hover/profile:scale-105 duration-300 shrink-0">
                                   {match.matched_avatar ? (
                                     <img src={match.matched_avatar} alt={match.matched_username} className="w-full h-full object-cover" />
                                   ) : (
                                     <div className="w-full h-full flex items-center justify-center text-slate-600">
                                        <i className="fas fa-user"></i>
                                     </div>
                                   )}
                                </div>
                                <div>
                                  <h4 className="text-white font-black italic group-hover/profile:text-purple-400 transition-colors flex items-center gap-1.5">
                                    <span>{match.fullname || match.matched_username}</span>
                                    {match.vip && (
                                      <span className="text-[9px] bg-amber-500/20 text-amber-300 font-bold px-1.5 py-0.2 rounded border border-amber-500/30">VIP</span>
                                    )}
                                    <i className="fas fa-arrow-up-right-from-square text-[9px] opacity-0 group-hover/profile:opacity-100 transition-all text-purple-400"></i>
                                  </h4>
                                  <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">@{match.matched_username}</p>
                                </div>
                             </Link>

                             {/* Badges de Combinação */}
                             <div className="flex flex-wrap items-center gap-2">
                               {match.is_mutual && (
                                 <div className="px-2.5 py-1 bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-500/30 rounded-full flex items-center gap-1.5">
                                   <i className="fas fa-handshake text-purple-400 text-xs"></i>
                                   <span className="text-[10px] font-black text-purple-300 uppercase">Troca Mútua</span>
                                 </div>
                               )}
                               {hasWishlist && (
                                 <div className="px-2.5 py-1 bg-pink-500/10 border border-pink-500/20 rounded-full flex items-center gap-1.5">
                                   <i className="fas fa-heart text-pink-400 text-[10px]"></i>
                                   <span className="text-[10px] font-black text-pink-400 uppercase">{match.wishlist_matches.length} na sua Wishlist</span>
                                 </div>
                               )}
                               {hasOfferlist && (
                                 <div className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full flex items-center gap-1.5">
                                   <i className="fas fa-sparkles text-emerald-400 text-[10px]"></i>
                                   <span className="text-[10px] font-black text-emerald-400 uppercase">{match.offerlist_matches.length} na sua Offerlist</span>
                                 </div>
                               )}
                             </div>
                          </div>

                          {/* Seção 1: Cartas da sua Wishlist que ele possui na Offerlist dele */}
                          {hasWishlist && (matchFilter === 'all' || matchFilter === 'wishlist_to_offer' || matchFilter === 'mutual') && (
                            <div className="space-y-2 mb-4 bg-slate-950/40 border border-pink-500/15 rounded-2xl p-3 sm:p-4">
                              <div className="flex items-center justify-between">
                                <p className="text-[10px] font-black uppercase text-pink-400 tracking-wider flex items-center gap-1.5">
                                  <i className="fas fa-gift"></i>
                                  <span>Ele possui da sua Wishlist ({match.wishlist_matches.length}):</span>
                                </p>
                                <span className="text-[9px] text-slate-500 uppercase font-mono">Disponíveis p/ você</span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {match.wishlist_matches.slice(0, 6).map((card: any, cidx: number) => (
                                  <div key={cidx} className="bg-slate-900 border border-pink-500/20 rounded-xl px-2.5 py-1 flex items-center space-x-2">
                                    <i className="fas fa-heart text-[8px] text-pink-400"></i>
                                    <span className="text-xs font-bold text-slate-200">{card.name}</span>
                                    {card.set_code && <span className="text-[9px] font-mono text-slate-500">[{card.set_code}]</span>}
                                  </div>
                                ))}
                                {match.wishlist_matches.length > 6 && (
                                  <div className="bg-slate-800/50 rounded-xl px-2.5 py-1 flex items-center">
                                    <span className="text-xs font-bold text-slate-500">+{match.wishlist_matches.length - 6} mais</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Seção 2: Cartas da sua Offerlist que ele deseja na Wishlist dele */}
                          {hasOfferlist && (matchFilter === 'all' || matchFilter === 'offer_to_wish' || matchFilter === 'mutual') && (
                            <div className="space-y-2 mb-5 bg-slate-950/40 border border-emerald-500/15 rounded-2xl p-3 sm:p-4">
                              <div className="flex items-center justify-between">
                                <p className="text-[10px] font-black uppercase text-emerald-400 tracking-wider flex items-center gap-1.5">
                                  <i className="fas fa-tag"></i>
                                  <span>Ele deseja da sua Offerlist ({match.offerlist_matches.length}):</span>
                                </p>
                                <span className="text-[9px] text-slate-500 uppercase font-mono">Suas cartas p/ troca</span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {match.offerlist_matches.slice(0, 6).map((card: any, cidx: number) => (
                                  <div key={cidx} className="bg-slate-900 border border-emerald-500/20 rounded-xl px-2.5 py-1 flex items-center space-x-2">
                                    <i className="fas fa-sparkles text-[8px] text-emerald-400"></i>
                                    <span className="text-xs font-bold text-slate-200">{card.name}</span>
                                    {card.set_code && <span className="text-[9px] font-mono text-slate-500">[{card.set_code}]</span>}
                                  </div>
                                ))}
                                {match.offerlist_matches.length > 6 && (
                                  <div className="bg-slate-800/50 rounded-xl px-2.5 py-1 flex items-center">
                                    <span className="text-xs font-bold text-slate-500">+{match.offerlist_matches.length - 6} mais</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {hasPendingRequest(match.matched_user_id) ? (
                            <button 
                              disabled
                              className="w-full bg-slate-900 border border-slate-800 text-slate-500 font-black py-3 rounded-2xl flex items-center justify-center space-x-2 text-xs uppercase tracking-widest cursor-not-allowed opacity-60"
                            >
                               <i className="fas fa-clock text-slate-500"></i>
                               <span>Contato Solicitado (Pendente)</span>
                            </button>
                          ) : (
                            <button 
                              onClick={() => handleOpenContactModalFromMatch(match, matchFilter)}
                              className="w-full bg-slate-950 hover:bg-purple-600/20 border border-slate-800 hover:border-purple-600/50 text-white font-black py-3 rounded-2xl transition-all flex items-center justify-center space-x-2 text-xs uppercase tracking-widest cursor-pointer group-hover:border-purple-500/50 shadow-md"
                            >
                               <i className="fas fa-paper-plane text-purple-400"></i>
                               <span>Solicitar Contato & Marcar Cartas</span>
                            </button>
                          )}
                       </div>
                     );
                   })}
                 </div>
               ) : (
                 <div className="bg-slate-900/50 border border-slate-800 border-dashed rounded-3xl p-12 sm:p-16 text-center space-y-4">
                    <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto text-slate-600 mb-4">
                      <i className="fas fa-filter text-2xl"></i>
                    </div>
                    <h3 className="font-bold text-white text-base">
                      {matchFilter === 'wishlist_to_offer' 
                        ? 'Nenhum usuário com cartas da sua Wishlist na Offerlist dele'
                        : matchFilter === 'offer_to_wish'
                        ? 'Nenhum usuário procurando cartas da sua Offerlist na Wishlist dele'
                        : matchFilter === 'mutual'
                        ? 'Nenhuma oportunidade de troca 100% mútua no momento'
                        : 'Nenhuma oportunidade automática encontrada'}
                    </h3>
                    <p className="text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
                      {matchFilter === 'wishlist_to_offer'
                        ? 'Adicione mais cartas que você está procurando à sua Wishlist para que o CardLink encontre outros colecionadores com ofertas correspondentes.'
                        : matchFilter === 'offer_to_wish'
                        ? 'Adicione cartas que você tem disponíveis para troca na sua Offerlist para que outros colecionadores que as desejam te encontrem!'
                        : matchFilter === 'mutual'
                        ? 'Uma troca mútua ocorre quando ambos têm interesse recíproco nas cartas do outro. Você pode explorar quem possui sua Wishlist ou quem deseja sua Offerlist nos filtros acima.'
                        : 'Adicione cartas à sua Wishlist e à sua Offerlist para que o CardLink calcule as oportunidades automaticamente.'}
                    </p>
                    <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
                      {matchFilter !== 'all' && (
                        <button
                          type="button"
                          onClick={() => setMatchFilter('all')}
                          className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white font-bold px-4 py-2.5 rounded-2xl text-xs transition-colors cursor-pointer"
                        >
                          <i className="fas fa-arrows-rotate text-xs"></i>
                          <span>Ver todas as {countAllMatches} oportunidades</span>
                        </button>
                      )}
                      <button
                        onClick={() => setActiveTab('users')}
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-black px-5 py-2.5 rounded-2xl text-xs uppercase tracking-wider transition-all shadow-lg shadow-purple-600/20 active:scale-95 cursor-pointer"
                      >
                        <i className="fas fa-users"></i>
                        <span>Ver Colecionadores da Comunidade</span>
                      </button>
                    </div>
                 </div>
               )}
             </div>
           ) : activeTab === 'active' ? (
             /* 2. ABA ATIVAS (SOLICITAÇÕES DE CONTATO) */
             <div className="space-y-8">
               {loadingRequests ? (
                 <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-12 text-center">
                    <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Carregando solicitações...</p>
                 </div>
               ) : (
                 <>
                   {/* Incoming Direct Contact Requests */}
                   <div className="space-y-4">
                     <h3 className="text-sm font-black uppercase text-slate-400 tracking-widest flex items-center gap-2">
                       <i className="fas fa-arrow-down text-purple-400"></i>
                       <span>Recebidas</span>
                       <span className="bg-purple-500/10 text-purple-400 border border-purple-500/20 text-[10px] font-mono font-black px-2.5 py-0.5 rounded-full inline-flex items-center justify-center min-w-[20px] h-5 ml-1">
                         {activeIncoming.length}
                       </span>
                     </h3>
                     {activeIncoming.length > 0 ? (
                       <div className="grid grid-cols-1 gap-4">
                         {activeIncoming.map((req, idx) => {
                           const parsed = parseContactMessage(req.message);

                           return (
                             <div key={idx} className="bg-slate-900 border border-slate-800 rounded-3xl p-6 hover:border-purple-500/20 transition-all space-y-4">
                               <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                 <div className="flex items-center space-x-3">
                                   <Link to={`/perfil/${req.requester?.id}`} className="w-11 h-11 bg-slate-800 rounded-xl overflow-hidden border border-white/5 cursor-pointer block shrink-0">
                                     {req.requester?.avatar ? (
                                        <img src={req.requester.avatar} alt={req.requester.username} className="w-full h-full object-cover" />
                                     ) : (
                                        <div className="w-full h-full flex items-center justify-center text-slate-600">
                                          <i className="fas fa-user"></i>
                                        </div>
                                     )}
                                   </Link>
                                   <div>
                                     <h4 className="text-white font-black text-sm italic">
                                       <Link to={`/perfil/${req.requester?.id}`} className="hover:text-purple-400 transition-colors">
                                         {req.requester?.fullname || req.requester?.username || 'Usuário'}
                                       </Link>
                                     </h4>
                                     <p className="text-[10px] text-slate-500 font-bold">@{req.requester?.username}</p>
                                   </div>
                                 </div>
                                 <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider bg-slate-950 px-2.5 py-1 rounded-lg self-start sm:self-center">
                                   {new Date(req.created_at).toLocaleDateString()}
                                 </span>
                               </div>

                               {/* Cartas Marcadas de Interesse */}
                               {parsed.cards.length > 0 && (
                                 <div className="bg-purple-950/20 border border-purple-500/20 rounded-2xl p-4 space-y-2">
                                   <div className="flex items-center gap-2">
                                     <i className="fas fa-bookmark text-purple-400 text-xs"></i>
                                     <span className="text-[11px] font-black uppercase text-purple-300 tracking-wider">
                                       Cartas de interesse marcadas ({parsed.cards.length})
                                     </span>
                                   </div>
                                   <div className="flex flex-wrap gap-1.5">
                                     {parsed.cards.map((cardName, cIdx) => (
                                       <span key={cIdx} className="bg-slate-950/80 border border-purple-500/30 text-white font-bold text-xs px-2.5 py-1 rounded-xl flex items-center gap-1.5">
                                         <i className="fas fa-check text-[8px] text-purple-400"></i>
                                         {cardName}
                                       </span>
                                     ))}
                                   </div>
                                 </div>
                               )}

                               {/* Mensagem personalizada */}
                               {parsed.text && (
                                 <div className="bg-slate-950 border border-white/5 p-4 rounded-2xl text-xs text-slate-300 leading-relaxed italic">
                                   "{parsed.text}"
                                 </div>
                               )}

                                {/* Aviso de Privacidade: Contato Protegido */}
                                <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-3.5 flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 flex items-center justify-center text-xs shrink-0">
                                    <i className="fas fa-shield-halved"></i>
                                  </div>
                                  <p className="text-[11px] text-slate-400 leading-snug">
                                    <strong className="text-slate-200">Número de contato protegido:</strong> Os números de WhatsApp de ambas as partes só ficam visíveis se você aceitar a solicitação.
                                  </p>
                                </div>

                                {/* Ações */}
                                <div className="flex flex-wrap gap-2 pt-1">
                                  <button 
                                    onClick={() => handleUpdateStatus(req, 'accepted')}
                                    className="flex-1 bg-purple-600 hover:bg-purple-500 text-white font-black py-2.5 rounded-xl transition-all text-xs uppercase tracking-wider cursor-pointer flex items-center justify-center gap-2 active:scale-95"
                                  >
                                    <i className="fas fa-check text-xs"></i>
                                    <span>Aceitar Contato</span>
                                  </button>
                                  <button 
                                    onClick={() => handleUpdateStatus(req, 'rejected')}
                                    className="flex-1 bg-slate-950 hover:bg-red-950 border border-slate-800 hover:border-red-900 text-slate-400 hover:text-red-400 font-black py-2.5 rounded-xl transition-all text-xs uppercase tracking-wider cursor-pointer flex items-center justify-center gap-2 active:scale-95"
                                  >
                                    <i className="fas fa-times text-xs"></i>
                                    <span>Recusar</span>
                                  </button>
                                </div>
                             </div>
                           );
                         })}
                       </div>
                     ) : (
                       <p className="text-xs text-slate-500 italic bg-slate-900/20 border border-slate-800/40 rounded-2xl p-6 text-center">Nenhuma solicitação de contato recebida pendente.</p>
                     )}
                   </div>

                   {/* Outgoing Direct Contact Requests */}
                   <div className="space-y-4 pt-4">
                     <h3 className="text-sm font-black uppercase text-slate-400 tracking-widest flex items-center gap-2">
                       <i className="fas fa-arrow-up text-pink-400"></i>
                       <span>Enviadas</span>
                       <span className="bg-pink-500/10 text-pink-400 border border-pink-500/20 text-[10px] font-mono font-black px-2.5 py-0.5 rounded-full inline-flex items-center justify-center min-w-[20px] h-5 ml-1">
                         {activeOutgoing.length}
                       </span>
                     </h3>
                     {activeOutgoing.length > 0 ? (
                       <div className="grid grid-cols-1 gap-4">
                         {activeOutgoing.map((req, idx) => {
                           const parsed = parseContactMessage(req.message);

                           return (
                             <div key={idx} className="bg-slate-900 border border-slate-800 rounded-3xl p-6 hover:border-pink-500/20 transition-all space-y-4">
                               <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                 <div className="flex items-center space-x-3">
                                   <Link to={`/perfil/${req.receiver?.id}`} className="w-11 h-11 bg-slate-800 rounded-xl overflow-hidden border border-white/5 cursor-pointer block shrink-0">
                                     {req.receiver?.avatar ? (
                                        <img src={req.receiver.avatar} alt={req.receiver.username} className="w-full h-full object-cover" />
                                     ) : (
                                        <div className="w-full h-full flex items-center justify-center text-slate-600">
                                          <i className="fas fa-user"></i>
                                        </div>
                                     )}
                                   </Link>
                                   <div>
                                     <h4 className="text-white font-black text-sm italic">
                                       <Link to={`/perfil/${req.receiver?.id}`} className="hover:text-pink-400 transition-colors">
                                         {req.receiver?.fullname || req.receiver?.username || 'Usuário'}
                                       </Link>
                                     </h4>
                                     <p className="text-[10px] text-slate-500 font-bold">@{req.receiver?.username}</p>
                                   </div>
                                 </div>
                                 <div className="flex items-center gap-2 self-start sm:self-center">
                                   <span className="text-[9px] font-black uppercase text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2.5 py-0.5 rounded-full">
                                     Aguardando Resposta
                                   </span>
                                   <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider bg-slate-950 px-2.5 py-1 rounded-lg">
                                     {new Date(req.created_at).toLocaleDateString()}
                                   </span>
                                 </div>
                               </div>

                               {/* Cartas Marcadas de Interesse */}
                               {parsed.cards.length > 0 && (
                                 <div className="bg-purple-950/20 border border-purple-500/20 rounded-2xl p-4 space-y-2">
                                   <div className="flex items-center gap-2">
                                     <i className="fas fa-bookmark text-purple-400 text-xs"></i>
                                     <span className="text-[11px] font-black uppercase text-purple-300 tracking-wider">
                                       Suas cartas marcadas de interesse ({parsed.cards.length})
                                     </span>
                                   </div>
                                   <div className="flex flex-wrap gap-1.5">
                                     {parsed.cards.map((cardName, cIdx) => (
                                       <span key={cIdx} className="bg-slate-950/80 border border-purple-500/30 text-white font-bold text-xs px-2.5 py-1 rounded-xl flex items-center gap-1.5">
                                         <i className="fas fa-check text-[8px] text-purple-400"></i>
                                         {cardName}
                                       </span>
                                     ))}
                                   </div>
                                 </div>
                               )}

                               {parsed.text && (
                                 <div className="bg-slate-950 border border-white/5 p-4 rounded-2xl text-xs text-slate-300 italic">
                                   "{parsed.text}"
                                 </div>
                               )}

                                {/* Aviso de Privacidade: Contato Protegido */}
                                <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-3.5 flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-xl bg-pink-500/10 border border-pink-500/20 text-pink-400 flex items-center justify-center text-xs shrink-0">
                                    <i className="fas fa-lock"></i>
                                  </div>
                                  <p className="text-[11px] text-slate-400 leading-snug">
                                    <strong className="text-slate-200">Aguardando aceite do colecionador:</strong> Os números de contato de ambas as partes só ficam visíveis se a solicitação for aceita.
                                  </p>
                                </div>

                                <button 
                                 onClick={() => handleUpdateStatus(req, 'cancelled')}
                                 className="w-full bg-slate-950 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white font-black py-2.5 rounded-xl transition-all text-xs uppercase tracking-wider cursor-pointer"
                               >
                                 Cancelar Solicitação
                               </button>
                             </div>
                           );
                         })}
                       </div>
                     ) : (
                       <p className="text-xs text-slate-500 italic bg-slate-900/20 border border-slate-800/40 rounded-2xl p-6 text-center">Você não possui solicitações de contato enviadas em aberto.</p>
                     )}
                   </div>
                 </>
               )}
             </div>
           ) : activeTab === 'history' ? (
             /* 3. ABA HISTÓRICO */
             <div className="space-y-4">
               {loadingRequests ? (
                 <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-12 text-center">
                    <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Carregando histórico...</p>
                 </div>
               ) : historyRequests.length > 0 ? (
                 <div className="grid grid-cols-1 gap-4">
                   {historyRequests.map((req, idx) => {
                     const isRequester = req.requester_id === user?.id;
                     const partner = isRequester ? req.receiver : req.requester;
                     const parsed = parseContactMessage(req.message);
                      // Regra de privacidade CardLink:
                      // O número de contato de ambas as partes SÓ fica visível se a solicitação for aceita!
                      const isAccepted = req.status === 'accepted';
                      const partnerPhone = isAccepted 
                        ? (isRequester ? (parsed.receiverPhone || (partner as any)?.phone) : (parsed.requesterPhone || (partner as any)?.phone))
                        : null;
                      const mySharedPhone = isAccepted
                        ? (isRequester ? (parsed.requesterPhone || user?.phone) : (parsed.receiverPhone || user?.phone))
                        : null;
                      const waLink = isAccepted && partnerPhone ? getWhatsAppLink(partnerPhone, partner?.fullname || partner?.username) : null;
                     
                     const statusConfigs: { [key: string]: { label: string, color: string } } = {
                       accepted: { label: 'Contato Aceito', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
                       declined: { label: 'Recusado', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
                       rejected: { label: 'Recusado', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
                       cancelled: { label: 'Cancelado', color: 'text-slate-400 bg-slate-800/10 border-slate-800/20' },
                       canceled: { label: 'Cancelado', color: 'text-slate-400 bg-slate-800/10 border-slate-800/20' }
                     };
                     const config = statusConfigs[req.status] || { label: req.status, color: 'text-slate-400 bg-slate-800/10 border-slate-800/20' };

                     return (
                       <div key={idx} className="bg-slate-900 border border-slate-800/50 rounded-3xl p-6 space-y-4">
                         <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                           <div className="flex items-center space-x-3">
                             <Link to={`/perfil/${partner?.id}`} className="w-11 h-11 bg-slate-800 rounded-xl overflow-hidden border border-white/5 cursor-pointer block shrink-0">
                               {partner?.avatar ? (
                                 <img src={partner.avatar} alt={partner.username} className="w-full h-full object-cover" />
                               ) : (
                                 <div className="w-full h-full flex items-center justify-center text-slate-600">
                                   <i className="fas fa-user"></i>
                                 </div>
                               )}
                             </Link>
                             <div>
                               <h4 className="text-white font-black text-sm italic">
                                 <Link to={`/perfil/${partner?.id}`} className="hover:text-purple-400 transition-colors">
                                   {partner?.fullname || partner?.username || 'Usuário'}
                                 </Link>
                               </h4>
                               <p className="text-[10px] text-slate-500 font-bold">
                                 {isRequester ? 'Enviada por você' : 'Enviada para você'}
                               </p>
                             </div>
                           </div>

                           <div className="flex sm:flex-col items-start sm:items-end justify-between gap-2">
                             <span className={`text-[9px] font-black uppercase border px-2.5 py-0.5 rounded-full ${config.color}`}>
                               {config.label}
                             </span>
                             <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">
                               {new Date(req.created_at).toLocaleDateString()}
                             </span>
                           </div>
                         </div>

                         {/* Cartas marcadas registradas */}
                         {parsed.cards.length > 0 && (
                           <div className="bg-slate-950/60 border border-white/5 rounded-2xl p-3.5 space-y-1.5">
                             <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Cartas marcadas ({parsed.cards.length}):</p>
                             <div className="flex flex-wrap gap-1.5">
                               {parsed.cards.map((cName, cIdx) => (
                                 <span key={cIdx} className="bg-slate-900 border border-white/10 text-slate-200 text-xs font-bold px-2 py-0.5 rounded-lg">
                                   {cName}
                                 </span>
                               ))}
                             </div>
                           </div>
                         )}

                          {/* Contato de ambas as partes (SOMENTE SE ACEITO) */}
                          {isAccepted && (
                            <div className="pt-1 bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-2xl space-y-3">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="space-y-1 min-w-0">
                                  <div className="flex items-center gap-2 text-emerald-400 font-black text-xs uppercase tracking-wider">
                                    <i className="fab fa-whatsapp text-sm"></i>
                                    <span>Contato Liberado (Ambas as Partes)</span>
                                  </div>
                                  <p className="text-white text-xs sm:text-sm font-bold truncate">
                                    WhatsApp de @{partner?.username || 'colecionador'}:{' '}
                                    {partnerPhone ? (
                                      <span className="font-mono text-emerald-300 font-black">{partnerPhone}</span>
                                    ) : (
                                      <span className="text-slate-400 font-normal italic text-xs">Não cadastrado pelo usuário</span>
                                    )}
                                  </p>
                                  {mySharedPhone && (
                                    <p className="text-[11px] text-slate-400">
                                      Seu WhatsApp compartilhado: <span className="font-mono text-slate-300 font-bold">{mySharedPhone}</span>
                                    </p>
                                  )}
                                </div>
                                {waLink && (
                                  <a 
                                    href={waLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-emerald-600 hover:bg-emerald-500 text-white font-black px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 shrink-0 shadow-lg shadow-emerald-600/20 active:scale-95 cursor-pointer whitespace-nowrap"
                                  >
                                    <i className="fab fa-whatsapp text-sm"></i>
                                    <span>Conversar no WhatsApp</span>
                                  </a>
                                )}
                              </div>
                            </div>
                          )}
                       </div>
                     );
                   })}
                 </div>
               ) : (
                 <div className="bg-slate-900/50 border border-slate-800 border-dashed rounded-3xl p-16 text-center space-y-4">
                    <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto text-slate-600 mb-4">
                      <i className="fas fa-right-left text-2xl"></i>
                    </div>
                    <h3 className="font-bold text-white leading-none">Nenhum contato no histórico</h3>
                    <p className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">Suas solicitações aceitas, recusadas ou canceladas aparecerão aqui.</p>
                 </div>
               )}
             </div>
           ) : activeTab === 'users' ? (
             /* 4. ABA COLECIONADORES (BUSCA VIA GET_SAFE_USERS) */
             <div className="space-y-6">
               {/* Search & Filters */}
               <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
                 <div className="relative flex-1">
                   <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
                   <input
                     type="text"
                     value={userSearchTerm}
                     onChange={(e) => setUserSearchTerm(e.target.value)}
                     placeholder="Buscar colecionadores por @username ou nome..."
                     className="w-full bg-slate-900 border border-slate-800 rounded-2xl pl-10 pr-10 py-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
                   />
                   {userSearchTerm && (
                     <button 
                       onClick={() => setUserSearchTerm('')}
                       className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white cursor-pointer"
                     >
                       <i className="fas fa-times text-xs"></i>
                     </button>
                   )}
                 </div>
                 
                 <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-800 p-1 rounded-2xl shrink-0">
                   <button
                     onClick={() => setUserFilter('all')}
                     className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                       userFilter === 'all' ? 'bg-purple-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
                     }`}
                   >
                     Todos
                   </button>
                   <button
                     onClick={() => setUserFilter('offers')}
                     className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                       userFilter === 'offers' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
                     }`}
                   >
                     Com Offerlist
                   </button>
                   <button
                     onClick={() => setUserFilter('wishlist')}
                     className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                       userFilter === 'wishlist' ? 'bg-pink-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
                     }`}
                   >
                     Com Wishlist
                   </button>
                 </div>
               </div>

               {loadingSafeUsers ? (
                 <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-12 text-center">
                    <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Carregando colecionadores da comunidade...</p>
                 </div>
               ) : filteredUsers.length > 0 ? (
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                   {filteredUsers.map((u: any) => {
                     const isPending = hasPendingRequest(u.id);
                     return (
                       <div key={u.id} className="bg-slate-900 border border-slate-800/70 hover:border-purple-500/30 rounded-3xl p-5 transition-all flex flex-col justify-between gap-4 group">
                         <div className="flex items-start justify-between gap-3">
                           <Link to={`/perfil/${u.id}`} className="flex items-center space-x-3 group/prof min-w-0 flex-1">
                             <div className="w-12 h-12 bg-slate-800 rounded-2xl overflow-hidden border border-white/5 shrink-0 group-hover/prof:scale-105 transition-transform">
                               {u.avatar ? (
                                 <img src={u.avatar} alt={u.username} className="w-full h-full object-cover" />
                               ) : (
                                 <div className="w-full h-full flex items-center justify-center text-slate-600">
                                   <i className="fas fa-user"></i>
                                 </div>
                               )}
                             </div>
                             <div className="min-w-0">
                               <h4 className="text-white font-black italic group-hover/prof:text-purple-400 transition-colors truncate flex items-center gap-1.5">
                                 <span>{u.fullname || u.username}</span>
                                 {u.vip && (
                                   <span className="text-[9px] bg-amber-500/20 text-amber-300 font-bold px-1.5 py-0.5 rounded border border-amber-500/30 shrink-0">VIP</span>
                                 )}
                               </h4>
                               <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider truncate">@{u.username}</p>
                             </div>
                           </Link>
                         </div>

                         <div className="grid grid-cols-3 gap-2 bg-slate-950/70 rounded-2xl p-2.5 border border-white/5 text-center">
                           <div>
                             <p className="text-[8px] sm:text-[9px] text-slate-500 font-bold uppercase tracking-wider">Coleção</p>
                             <p className="text-xs font-black text-white">{u.collection_size || 0}</p>
                           </div>
                           <div>
                             <p className="text-[8px] sm:text-[9px] text-emerald-400 font-bold uppercase tracking-wider">Ofertas</p>
                             <p className="text-xs font-black text-emerald-400">{u.offers_size || 0}</p>
                           </div>
                           <div>
                             <p className="text-[8px] sm:text-[9px] text-pink-400 font-bold uppercase tracking-wider">Desejos</p>
                             <p className="text-xs font-black text-pink-400">{u.wishlist_size || 0}</p>
                           </div>
                         </div>

                         <div className="flex gap-2">
                           {isPending ? (
                             <button 
                               disabled
                               className="flex-1 bg-slate-900 border border-slate-800 text-slate-500 font-black py-2.5 rounded-xl text-[11px] uppercase tracking-wider cursor-not-allowed opacity-60"
                             >
                               Pendente
                             </button>
                           ) : (
                             <button 
                               onClick={() => handleOpenContactModalForUser(u)}
                               className="flex-1 bg-purple-600 hover:bg-purple-500 text-white font-black py-2.5 rounded-xl transition-all text-[11px] uppercase tracking-wider cursor-pointer active:scale-95 flex items-center justify-center gap-1.5"
                             >
                               <i className="fas fa-paper-plane text-[10px]"></i>
                               <span>Solicitar Contato</span>
                             </button>
                           )}
                         </div>
                       </div>
                     );
                   })}
                 </div>
               ) : (
                 <div className="bg-slate-900/50 border border-slate-800 border-dashed rounded-3xl p-16 text-center space-y-4">
                   <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto text-slate-600 mb-4">
                     <i className="fas fa-users-slash text-2xl"></i>
                   </div>
                   <h3 className="font-bold text-white leading-none">Nenhum colecionador encontrado</h3>
                   <p className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">
                     {userSearchTerm ? 'Tente buscar com outro nome ou username.' : 'Nenhum usuário corresponde aos filtros selecionados.'}
                   </p>
                 </div>
               )}
             </div>
           ) : null}
        </div>

        {/* Sidebar Status & Dicas */}
        <div className="space-y-6">
           <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl p-6 sm:p-8 text-white space-y-5 shadow-xl shadow-purple-600/10">
              <h2 className="text-lg sm:text-xl font-black italic">Como funciona o CardLink:</h2>
              <ul className="text-xs sm:text-sm space-y-3 opacity-95">
                <li className="flex items-start gap-2.5">
                  <i className="fas fa-check-circle text-emerald-300 mt-1 shrink-0"></i>
                  <span><strong>1. Marque as cartas:</strong> selecione no perfil ou match as cartas que você deseja negociar.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="fas fa-paper-plane text-purple-200 mt-1 shrink-0"></i>
                  <span><strong>2. Envie o contato:</strong> o colecionador recebe sua solicitação com a lista de cartas selecionadas.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="fab fa-whatsapp text-emerald-300 mt-1 shrink-0"></i>
                  <span><strong>3. Conecte-se:</strong> ao aceitar, vocês conversam no WhatsApp para fechar o negócio com total liberdade.</span>
                </li>
              </ul>
              <div className="flex items-center space-x-3 pt-2 border-t border-white/10">
                 <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center text-xs">
                    <i className="fas fa-handshake"></i>
                 </div>
                 <span className="text-[10px] font-black uppercase tracking-widest">CardLink Social Trading</span>
              </div>
           </div>

           <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6">
              <h3 className="font-black text-white text-sm uppercase tracking-widest">Seu Status</h3>
              <div className="grid grid-cols-2 gap-4">
                 <div className="bg-slate-950 p-4 rounded-2xl border border-white/5">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Contatos Feitos</p>
                    <p className="text-xl font-black text-white">
                       <span className="text-3xl font-black italic text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400 leading-none">
                         {historyRequests.filter((r: any) => r.status === 'accepted').length}
                       </span>
                     </p>
                 </div>
                 <div className="bg-slate-950 p-4 rounded-2xl border border-white/5">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Usuários</p>
                    <p className="text-xl font-black text-white">
                       <span className="text-3xl font-black italic text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400 leading-none">
                         {safeUsers.length}
                       </span>
                     </p>
                 </div>
                 <div className="bg-slate-950 p-4 rounded-2xl border border-white/5 col-span-2">
                    <div className="flex items-center justify-between gap-2">
                       <div className="min-w-0">
                          <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">WhatsApp / Telefone</p>
                          <p className="text-xs font-bold text-white truncate">
                             {hasPhone ? (
                                <span className="text-emerald-400 flex items-center gap-1.5">
                                   <i className="fas fa-check-circle text-xs"></i>
                                   <span className="truncate">{user?.phone}</span>
                                </span>
                             ) : (
                                <span className="text-amber-400 flex items-center gap-1.5">
                                   <i className="fas fa-exclamation-circle text-xs"></i>
                                   <span>Não cadastrado</span>
                                </span>
                             )}
                          </p>
                       </div>
                       {!hasPhone && (
                          <Link 
                            to="/perfil/editar"
                            className="text-[10px] font-black uppercase tracking-wider text-amber-400 hover:text-amber-300 bg-amber-500/10 border border-amber-500/20 px-3 py-1.5 rounded-xl transition-colors whitespace-nowrap shrink-0"
                          >
                            Adicionar
                          </Link>
                       )}
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </div>

      {/* Modal para Solicitação de Contato com Marcação de Cartas */}
      {selectedTarget && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 z-50 animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl relative animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="p-5 sm:p-6 border-b border-slate-800 flex items-center justify-between shrink-0 bg-slate-900/50">
              <div className="space-y-1">
                <h3 className="font-black text-lg sm:text-xl italic text-white flex items-center gap-2">
                  <i className="fas fa-paper-plane text-purple-400 text-base"></i>
                  Solicitação de Contato
                </h3>
                <p className="text-xs text-slate-400">
                  Marque as cartas de seu interesse e envie uma mensagem direta.
                </p>
              </div>
              <button 
                onClick={() => setSelectedTarget(null)}
                className="w-9 h-9 rounded-full bg-slate-950 hover:bg-slate-800 flex items-center justify-center text-slate-500 hover:text-white transition-colors cursor-pointer"
              >
                <i className="fas fa-times text-sm"></i>
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="p-5 sm:p-6 space-y-6 overflow-y-auto custom-scrollbar flex-1">
              {/* User Banner */}
              <div className="bg-slate-950 border border-white/5 rounded-2xl p-4 flex items-center space-x-3.5">
                <div className="w-12 h-12 bg-slate-800 rounded-xl overflow-hidden border border-white/5 shrink-0">
                  {selectedTarget.avatar ? (
                    <img src={selectedTarget.avatar} alt={selectedTarget.username} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-600">
                      <i className="fas fa-user"></i>
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-white font-black italic truncate">{selectedTarget.fullname || selectedTarget.username}</h4>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider truncate">@{selectedTarget.username}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-[10px] font-black uppercase text-purple-400 bg-purple-500/10 border border-purple-500/20 px-2.5 py-1 rounded-full">
                    {markedCardNames.length} {markedCardNames.length === 1 ? 'carta marcada' : 'cartas marcadas'}
                  </span>
                </div>
              </div>

              {/* SEÇÃO DE MARCAÇÃO DE CARTAS */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="fas fa-check-square text-purple-400 text-xs"></i>
                    <label className="text-[11px] font-black uppercase text-slate-300 tracking-wider">
                      Marcar Cartas de Interesse
                    </label>
                  </div>
                  {modalAvailableCards.length > 0 && (
                    <div className="flex items-center gap-2 text-[10px] font-bold">
                      <button 
                        type="button"
                        onClick={handleSelectAllCards}
                        className="text-purple-400 hover:text-purple-300 hover:underline cursor-pointer"
                      >
                        Marcar todas
                      </button>
                      <span className="text-slate-600">•</span>
                      <button 
                        type="button"
                        onClick={handleClearAllCards}
                        className="text-slate-500 hover:text-slate-300 hover:underline cursor-pointer"
                      >
                        Desmarcar
                      </button>
                    </div>
                  )}
                </div>

                {loadingModalCards ? (
                  <div className="bg-slate-950/60 border border-white/5 rounded-2xl p-6 text-center">
                    <div className="w-6 h-6 border-2 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Carregando cartas do usuário...</p>
                  </div>
                ) : modalAvailableCards.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto custom-scrollbar p-1">
                    {modalAvailableCards.map((card: any, idx: number) => {
                      const isMarked = markedCardNames.includes(card.name);
                      return (
                        <div
                          key={idx}
                          onClick={() => toggleCardMark(card.name)}
                          className={`flex items-center space-x-2.5 p-2.5 rounded-xl border transition-all cursor-pointer select-none text-left ${
                            isMarked 
                              ? 'bg-purple-600/15 border-purple-500/50 shadow-sm shadow-purple-500/5' 
                              : 'bg-slate-950/60 border-white/5 hover:border-slate-700 text-slate-400'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded-md flex items-center justify-center text-[10px] transition-colors shrink-0 ${
                            isMarked ? 'bg-purple-600 text-white' : 'border border-slate-700 bg-slate-900'
                          }`}>
                            {isMarked && <i className="fas fa-check"></i>}
                          </div>
                          {card.image_url ? (
                            <img src={card.image_url} alt={card.name} className="w-7 h-9 object-cover rounded shrink-0 bg-slate-900" />
                          ) : (
                            <div className="w-7 h-9 rounded bg-slate-800 flex items-center justify-center text-[10px] text-slate-500 shrink-0">
                              <i className="fas fa-image"></i>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className={`text-xs font-bold truncate ${isMarked ? 'text-white' : 'text-slate-300'}`}>
                              {card.name}
                            </p>
                            {card.set_code && (
                              <p className="text-[9px] text-slate-500 font-mono truncate">{card.set_code}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-slate-950/60 border border-white/5 rounded-2xl p-4 text-xs text-slate-400 leading-relaxed space-y-1">
                    <p className="font-bold text-slate-300">Nenhuma carta listada previamente</p>
                    <p className="text-[11px] text-slate-500">
                      Você pode adicionar manualmente o nome das cartas que tem interesse no campo abaixo.
                    </p>
                  </div>
                )}

                {/* Adicionar outra carta manualmente */}
                <div className="flex gap-2 pt-1">
                  <div className="relative flex-1">
                    <i className="fas fa-plus absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-[10px]"></i>
                    <input
                      type="text"
                      value={customCardInput}
                      onChange={(e) => setCustomCardInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCustomCard();
                        }
                      }}
                      placeholder="Adicionar outra carta por nome..."
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-8 pr-3 py-2 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddCustomCard}
                    disabled={!customCardInput.trim()}
                    className="bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white font-bold px-3 py-2 rounded-xl text-xs transition-colors cursor-pointer"
                  >
                    Adicionar
                  </button>
                </div>

                {/* Chips com as cartas marcadas */}
                {markedCardNames.length > 0 && (
                  <div className="pt-1 space-y-1.5">
                    <p className="text-[10px] font-black uppercase text-purple-400 tracking-wider">
                      Resumo das cartas marcadas ({markedCardNames.length}):
                    </p>
                    <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto custom-scrollbar">
                      {markedCardNames.map((name, idx) => (
                        <span 
                          key={idx} 
                          className="bg-purple-500/10 border border-purple-500/30 text-purple-300 text-[11px] font-bold px-2.5 py-1 rounded-xl flex items-center gap-1.5"
                        >
                          <span>{name}</span>
                          <button
                            type="button"
                            onClick={() => toggleCardMark(name)}
                            className="text-purple-400 hover:text-white ml-0.5 cursor-pointer"
                          >
                            <i className="fas fa-times text-[9px]"></i>
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Mensagem personalizada */}
              <div className="space-y-2">
                <label className="text-[11px] font-black uppercase text-slate-300 tracking-wider flex items-center gap-2">
                  <i className="fas fa-comment-dots text-purple-400 text-xs"></i>
                  <span>Mensagem para o Colecionador</span>
                </label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Escreva sua mensagem de contato..."
                  rows={3}
                  className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-purple-500 transition-colors resize-none"
                  disabled={isSubmitting}
                />
              </div>

              {/* Informação sobre contato WhatsApp */}
              <div className="bg-slate-950/80 border border-white/5 rounded-2xl p-4 flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0 mt-0.5">
                  <i className="fab fa-whatsapp text-sm"></i>
                </div>
                <div className="space-y-1 text-xs">
                  <p className="font-bold text-white">
                    {hasPhone ? `Seu WhatsApp (${user?.phone}) será compartilhado` : 'Você não possui WhatsApp cadastrado'}
                  </p>
                  <p className="text-slate-400 text-[11px] leading-relaxed">
                    {hasPhone 
                      ? 'Ao aceitar a solicitação, o colecionador terá acesso ao seu número para conversarem diretamente.'
                      : 'Recomendamos cadastrar seu telefone no perfil para que a outra pessoa consiga te chamar no WhatsApp ao aceitar.'}
                  </p>
                  {!hasPhone && (
                    <Link
                      to="/perfil/editar"
                      className="inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 font-black uppercase text-[10px] tracking-wider pt-1 hover:underline"
                    >
                      <span>Cadastrar telefone agora</span>
                      <i className="fas fa-arrow-right text-[8px]"></i>
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 sm:p-6 border-t border-slate-800 bg-slate-950/40 flex gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setSelectedTarget(null)}
                className="flex-1 bg-slate-950 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white font-black py-3 rounded-2xl transition-all text-xs uppercase tracking-widest cursor-pointer"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSendContactRequest}
                className="flex-1 bg-purple-600 hover:bg-purple-500 text-white font-black py-3 rounded-2xl transition-all shadow-lg shadow-purple-600/20 active:scale-95 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2 text-xs uppercase tracking-widest cursor-pointer"
                disabled={isSubmitting || (markedCardNames.length === 0 && !customMessage.trim())}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin"></div>
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane text-xs"></i>
                    <span>Solicitar Contato ({markedCardNames.length})</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
